<template>
  <Teleport to="body">
    <div>
      <Transition name="rs-modal-fade">
        <div
          v-show="input"
          :id="id ? String(id) : undefined"
          ref="rsModal"
          class="modal fade rs-modal"
          :class="[className, modalClass, { show: input }]"
          tabindex="-1"
          role="dialog"
          @mousedown="onBackdropMousedown"
          @click.self="onBackdropClick"
        >
          <div
            class="modal-dialog"
            :class="[
              dialogClass,
              size ? `modal-${size}` : '',
              { 'modal-dialog-centered': isCentered, 'modal-dialog-scrollable': scrollable },
            ]"
            role="document"
          >
            <div class="modal-content">
              <div class="modal-header">
                <div class="modal-header__left">
                  <RsIcon
                    v-show="infoIcon"
                    :class="`fas mr-1 ${iconClass}`"
                    style="color:DodgerBlue; font-size: 25px;"
                    icon="info-circle"
                  />
                  <RsIcon
                    v-if="icon"
                    :icon="icon"
                    style="font-weight: bold; margin-right: 5px; font-size: 25px;"
                    :class="iconClass"
                  />
                  <slot name="modalTitle">
                    <h5
                      class="modal-title"
                      v-html="title"
                    />
                  </slot>
                  <slot name="headerRight" />
                </div>
                <div class="modal-header__right">
                  <div class="modal-actions">
                    <RsDropdownActionMenu
                      v-show="dropdownItems.length > 0"
                      horizontal
                    >
                      <RsDropdownItem
                        v-for="(item, idx) in dropdownItems"
                        v-show="!item.isHidden"
                        :key="idx"
                        :title="item.title"
                        :delete="item.isDelete"
                        @click="$emit(item.emitName as any, item.emitData)"
                      />
                    </RsDropdownActionMenu>

                    <div
                      v-show="dropdownItems.length > 0"
                      class="modal-actions__separator"
                    />
                    <RsButton
                      v-if="headerButton && noCloseButton"
                      class="reset"
                      text
                      @click="$emit('on-header-button-clicked')"
                    >
                      {{ headerButton }}
                    </RsButton>
                    <button
                      v-if="!noCloseButton"
                      type="button"
                      title="Close"
                      aria-label="Close"
                      class="close"
                      @click="onCloseButtonClick"
                    >
                      <RsIcon icon="times" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="modal-body" :class="bodyClass">
                <div :key="openCount">
                  <slot v-if="!lazy || hasBeenShown" />
                </div>
              </div>

              <div class="modal-footer">
                <template v-if="isUpdatePermitted && showMainFooter">
                  <div class="modal-footer__left">
                    <slot name="modal-footer-left" />
                    <RsButton
                      v-if="headerButton && !noCloseButton"
                      class="rs-reset"
                      text
                      @click="$emit('on-header-button-clicked')"
                    >
                      {{ headerButton }}
                    </RsButton>

                    <RsButton
                      v-if="deleteButton"
                      :icon="deleteButton.icon"
                      :weight="deleteButton.iconWeight ?? 'far'"
                      :variant="deleteButton.variant"
                      @click="$emit(deleteButton.emitName as any, deleteButton.emitData)"
                    >
                      {{ deleteButton.text }}
                    </RsButton>
                    <RsButton
                      v-if="secondaryButton"
                      :variant="secondaryButtonVariant || 'primary'"
                      @click="$emit('on-secondary-button-clicked')"
                    >
                      {{ secondaryButton }}
                    </RsButton>
                    <span
                      v-if="lastUpdated"
                      class="small pr-3"
                    >
                      Last updated on {{ lastUpdatedFormatted }}
                    </span>
                    <span
                      v-if="customLeftFooterText !== ''"
                      class="small pr-3"
                    >
                      {{ customLeftFooterText }}
                    </span>
                    <RsToggleTranslation
                      v-if="isTranslatable"
                      use-select
                      :is-english-model-valid="isModalValid"
                      :edited-validator-object="editedValidatorObject"
                      :compare-to-validator-object="compareToValidatorObject"
                      @submit="$emit('submit')"
                    />
                    <span
                      v-if="$rsGlobals.$language !== 'en' && !hideLanguageWarning"
                      class="translation-text"
                    >Editing non-English content</span>
                  </div>
                  <div class="modal-footer__right">
                    <slot name="modal-footer">
                      <RsButton
                        v-if="isCancel"
                        :id="id ? `btn_${id}_cancel` : ''"
                        variant="cancel"
                        @click="onCloseButtonClick"
                      >
                        {{ $ts('actions.cancel') }}
                      </RsButton>
                      <RsButton
                        v-if="hasMainButtonRight"
                        :id="id ? `btn_${id}_submit` : ''"
                        :variant="secondaryButtonVariant"
                        :disabled="disabled"
                        @click="$emit('submit')"
                      >
                        <i
                          v-if="buttonIcon"
                          :class="`${buttonIcon} mr-2`"
                        />
                        {{ mainButtonText }}
                      </RsButton>
                    </slot>
                  </div>
                </template>
                <template v-else>
                  <div />
                </template>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <Transition name="rs-modal-fade">
        <div
          v-if="input"
          class="modal-backdrop fade show"
        />
      </Transition>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { IDropdownEmitOptions } from '@/shared';
import RsIcon from '@/components/_global/RsIcon.vue';

dayjs.extend(advancedFormat);

export type TDeleteButtonDetails<T = string> = {
  text: string,
  variant: string,
  icon: string,
  iconWeight?: string,
  emitName: string,
  emitData: T,
};

export default defineComponent({
  name: 'RsModal',
  components: { RsIcon },
  // The root node is a <Teleport>, so fallthrough attrs are never applied.
  // Consumers also bind the dynamic `dropdownItems`/`deleteButton` emit names,
  // which cannot be declared in `emits` — opting out keeps both off $attrs.
  inheritAttrs: false,
  props: {
    id: {
      type: [Number, String],
      default: '',
    },

    className: {
      type: String,
      default: '',
    },

    secondaryButtonVariant: {
      type: String,
      default: 'primary',
    },

    customLeftFooterText: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    icon: {
      type: String,
      default: '',
    },

    size: {
      type: String,
      default: '',
    }, // sm, md, lg, xl, etc.

    content: {
      type: String,
      default: '',
    },

    iconClass: {
      type: String,
      default: '',
    },

    infoIcon: {
      type: Boolean,
      default: false,
    },

    iconColor: {
      type: String,
      default: 'primary',
    },

    dataSaved: {
      type: Object,
      default: () => ({}),
    },

    tabIndex: {
      type: Number,
      default: 0,
    },

    tabs: {
      type: Boolean,
      default: false,
    },

    changed: {
      type: Boolean,
      default: false,
    },

    buttonText: {
      type: String,
      default: 'Save',
    },

    buttonIcon: {
      type: String,
      default: '',
    },

    secondaryButton: {
      type: String,
      default: '',
    },

    headerButton: {
      type: String,
      default: '',
    },

    deleteButton: {
      type: Object as PropType<TDeleteButtonDetails>,
      default: () => null,
    },

    lazy: {
      type: Boolean,
      default: true,
    },

    isModalValid: {
      type: Boolean,
      default: true,
    },

    isTranslatable: {
      type: Boolean,
      default: false,
    },

    isCancel: {
      type: Boolean,
      default: true,
    },

    value: {
      type: Boolean,
      default: false,
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: Boolean,
      default: undefined,
    },

    closeModal: {
      type: Boolean,
      default: false,
    },

    removableItems: {
      type: Array,
      default: () => [],
    },

    dropdownItems: {
      type: Array as PropType<IDropdownEmitOptions[]>,
      default: () => [],
    },

    isCompareDisabled: {
      type: Boolean,
      default: false,
    },

    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },

    noCloseButton: {
      type: Boolean,
      default: false,
    },

    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },

    editedValidatorObject: {
      type: Object,
      default: () => ({}),
    },

    compareToValidatorObject: {
      type: Object,
      default: () => ({}),
    },

    logObjectCompare: {
      type: Boolean,
      default: false,
    },

    lastUpdated: {
      type: [String, Date],
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    showMainFooter: {
      type: Boolean,
      default: true,
    },

    isCentered: {
      type: Boolean,
      default: false,
    },

    hasMainButtonRight: {
      type: Boolean,
      default: true,
    },

    scrollable: {
      type: Boolean,
      default: false,
    },

    dialogClass: {
      type: String,
      default: '',
    },

    modalClass: {
      type: String,
      default: '',
    },

    bodyClass: {
      type: String,
      default: '',
    },

    /**
     * Hide the non-english warning
     */
    hideLanguageWarning: {
      type: Boolean,
      default: false,
    },

    /**
     * Control whether the user has permission to update/save. When false, the footer with save button is hidden.
     */
    isUpdatePermitted: {
      type: Boolean,
      default: true,
    },

    /**
     * Stops mousedown events inside this modal from bubbling past its root element.
     * Since the modal teleports to <body>, interacting with it (e.g. its close button)
     * would otherwise reach `document`-level listeners belonging to unrelated code
     * outside this modal. Off by default — only opt in where that's an issue.
     *
     * Caveat: this also blocks components *inside* the modal that rely on a
     * document-level mousedown, e.g. `RsFormSelectSearchSingle`'s click-outside-to-close.
     * Don't enable this on a modal whose content (including slotted content) uses one.
    */
    stopMousedownPropagation: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['cancel', 'input', 'on-header-button-clicked', 'on-modal-close', 'on-reject-changes', 'on-secondary-button-clicked', 'shown', 'submit', 'update:modelValue'],
  data() {
    return {
      dataChange: false,
      showAlert: false,
      prevent: false,
      backdropMousedownOnSelf: false,
      input: this.modelValue !== undefined ? this.modelValue : this.value,
      translationKey: 0,
      hasBeenShown: this.modelValue !== undefined ? this.modelValue : this.value,
      openCount: 0,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): boolean {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    valid(): boolean {
      return !this.isModalDifferent;
    },

    mainButtonText(): string {
      // Preserve custom labels while localizing the component's default submit labels.
      if (this.buttonText === 'Save') {
        return this.$ts('actions.save') as string;
      }

      if (this.buttonText === 'Save Translations') {
        return this.$ts('translations.saveButton') as string;
      }

      return this.buttonText;
    },

    lastUpdatedFormatted(): string {
      return dayjs(this.lastUpdated).format('MMMM Do, YYYY');
    },
  },

  watch: {
    boundValue: {
      handler() {
        if (this.boundValue) {
          this.input = true;
        } else {
          this.handleHide();
        }
      },
    },
    closeModal: {
      immediate: true,
      handler() {
        if (this.closeModal) {
          this.handleHide();
        }
      },
    },
    input(newVal: boolean): void {
      if (newVal) {
        this.hasBeenShown = true;
        this.openCount += 1;
        document.body.classList.add('modal-open');
        this.$nextTick(() => { this.$emit('shown'); });
      } else {
        document.body.classList.remove('modal-open');
      }
    },
  },

  created() {
    if (this.input) {
      this.hasBeenShown = true;
      document.body.classList.add('modal-open');
      this.$nextTick(() => { this.$emit('shown'); });
    }
    (this as any)._onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.input && !this.noCloseOnEsc) {
        this.handleHide();
      }
    };
    document.addEventListener('keydown', (this as any)._onKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', (this as any)._onKeydown);
    document.body.classList.remove('modal-open');
  },
  methods: {
    async showWarningMsg(title: string = 'You have unsaved changes!') {
      return this.$swal
        .fire({
          title: `<i class="fas fa-exclamation-triangle rs-text-warning"></i> ${title}`,
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonColor: '#00a889',
          confirmButtonText: `Confirm`,
          denyButtonText: `Continue without saving`,
          customClass: {
            container: 'swal-warning-container swal-warning-no-text',
            htmlContainer: 'swal-warning-body-container',
            title: 'swal-warning-title',
            actions: 'swal-warning-actions',
          },
        })
        .then((result) => result);
    },

    async showErrorMsg() {
      return this.$swal
        .fire({
          icon: 'error',
          title: 'Form is invalid.',
          confirmButtonColor: '#dd6b55',
          confirmButtonText: `OK`,
        })
        .then((result) => result);
    },

    async showSuccessMsg() {
      return this.$swal
        .fire({
          icon: 'success',
          title: 'Saved!',
          confirmButtonColor: '#00a889',
          confirmButtonText: `OK`,
        })
        .then((result) => result);
    },

    /** Records whether the mousedown originated on the backdrop so drag-to-close is prevented. */
    onBackdropMousedown(e: MouseEvent): void {
      this.backdropMousedownOnSelf = e.target === e.currentTarget;
      // Bubble-phase, after any descendant (e.g. the close button) has already handled it —
      // this only stops it from reaching `document` above this modal's own root element.
      if (this.stopMousedownPropagation) e.stopPropagation();
    },

    /** Closes the modal only when both mousedown and mouseup occurred on the backdrop itself. */
    onBackdropClick(): void {
      if (!this.noCloseOnBackdrop && this.backdropMousedownOnSelf) {
        this.handleHide();
      }
    },

    async handleHide() {
      if (this.isModalDifferent() && this.input) {
        this.$rsGlobals.$rsSwal.warning({
          title: this.$rsGlobals.$isTranslateMode ? `You have unsaved translations!` : `${this.$ts('unsavedChanges.validTitle')}`,
          confirmHandler: async () => {
            if (this.isModalValid) {
              this.$emit('submit');
              await this.showSuccessMsg();
              this.closeModalManually();
            } else {
              await this.showErrorMsg();
              this.$emit('input', true);
              this.$emit('update:modelValue', true);
            }
          },
          rejectHandler: () => {
            this.closeModalManually();
            this.$emit('on-reject-changes');
          },
          confirmWithoutSaveHandler: () => {
            this.$emit('input', true);
            this.$emit('update:modelValue', true);
          },
          confirmButtonText: `${this.$ts('actions.confirm')}`,
          denyButtonText: `${this.$ts('unsavedChanges.continueWithoutSaving')}`,
        });
      } else {
        this.closeModalManually();
        this.$emit('on-reject-changes');
      }
    },

    closeModalManually(): void {
      this.input = false;
      this.$emit('input', false);
      this.$emit('update:modelValue', false);
      this.$emit('cancel');
    },

    onCloseButtonClick() {
      this.handleHide();
      this.$emit('on-modal-close');
    },

    isModalDifferent(): boolean {
      return !this.isCompareDisabled && this.$utils.isObjectDifferent(
        this.$utils.cloneObject(this.editedValidatorObject ?? {}),
        this.$utils.cloneObject(this.compareToValidatorObject ?? {}),
        false,
        this.logObjectCompare,
      );
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables.scss";
$modal-padding: 1.5rem;

// Vue 3 <Transition> classes restoring the BootstrapVue-era modal animation
// (Bootstrap 4.6 values: opacity 0.15s linear, dialog translate(0, -50px) over 0.3s ease-out).
// Bootstrap's own `.modal { display: none }` would win over v-show's cleared inline
// style, so the shown state needs an explicit display here.
.modal.rs-modal {
  display: block;
}
.rs-modal-fade-enter-active,
.rs-modal-fade-leave-active {
  transition: opacity 0.15s linear;
}
.modal.fade.rs-modal-fade-enter-from,
.modal.fade.rs-modal-fade-leave-to,
.modal-backdrop.fade.rs-modal-fade-enter-from,
.modal-backdrop.fade.rs-modal-fade-leave-to {
  opacity: 0;
}
.modal.fade.rs-modal-fade-enter-from .modal-dialog {
  transform: translate(0, -50px);
}
.modal-dialog {
  &.modal-fs {
    max-width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
  }
  &.modal-sp {
    max-width: 620px;
  }
  .modal-header {
    align-items: center;
    padding: 0.5rem $modal-padding;
    border-bottom: 1px #DEE2E6 solid;
    &__left {
      display: flex;
      align-items: center;
      h5 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        display: inline-block;
      }
    }
    &__right {
      display: flex;
      align-items: center;
    }
    .modal-actions {
      display: flex;
      align-items: center;
      .close {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        padding: 0;
        width: 40px;
        height: 40px;
        border-radius: $global-radius;
        color: $tertiary-muted;
        opacity: 0.4;
        transition: opacity 0.2s ease;
        &:hover {
          opacity: 1;
          background: $extra-light-gray;
        }
      }
      .reset {
        padding: 0;
        position: relative;
        font: normal normal bold 14px/19px Nunito;
        height: 19px;
        background: $white;
        color: $secondary-color;
        transition: opacity 0.2s ease;
        border: none;
        &:hover {
          background: $white;
        }
      }
      &__separator {
        width: 1px;
        height: 24px;
        background: rgba($extra-light-gray, 0.7);
        margin: 0 8px;
      }
      .rs-dropdown-action-menu {
        .dropdown-toggle {
          padding: 0;
          width: 40px;
          height: 40px;
          border-radius: $global-radius;
          opacity: 0.4;
          transition: all 0.2s ease;
          .rs-icon {
            color: $tertiary-muted;
          }
          &:hover {
            opacity: 1;
            background: $extra-light-gray;
          }
          &--open {
            background-color: $extra-light-gray;
            opacity: 1;
          }
        }
      }
    }
    .rs-avatar {
      width: 32px;
      height: 32px;
      font-size: 16px;
      margin-right: 0.5rem;
    }
  }
  .modal-body {
    min-height: 100%;
    padding: 1rem $modal-padding;
    p {
      font-size: 14px;
      color: $dark-gray;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .modal-footer {
    z-index: 1;
    background: $white;
    padding: 0.5rem $modal-padding;
    &__left {
      display: flex;
      align-items: center;
      margin-right: auto;
      .rs-button {
        margin-right: 1rem;
      }
      .rs-select {
        margin: 0;
        width: 60px;
      }
      .translation-text {
        margin-left: 1rem;
        color: $error;
        font-size: 0.875rem;
      }
      .rs-reset {
        padding: 0;
        height: 19px;
        color: $secondary-color;
        border: none;
        &:hover {
          color: $secondary-color;
          background: $white;
        }
      }
    }
    &__right {
      .rs-button {
        margin: 0;
        &--cancel {
          margin-right: 0.813rem;
        }
      }
    }
  }

  .title-link {
    color: $secondary-color;
    font-weight: 700;
    align-self: center;

    .text-underline {
      text-decoration: underline;
    }
  }
}

// Styling For All Filter Modals
.navigation-trial, .tours-filter-modal-body{
  .tab-content {
    border-left: 1px solid #DBDBDB;
    padding-right: 24px;
  }
  .nav-tabs .nav-item:last-child {
    border-bottom: none !important;
  }
}

.navigation-trial > div:first-child, .tours-filter-modal-body > div:first-child{
  ul {
    li a {
      border-radius: 0;
      border: 0;
    }
  }
}

</style>
